import React, { useMemo, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ApplicationModel, ApplicationPaymentModel } from '@/models/application.model'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { admissionsService } from '@/services/admissionsService'
import { paymentsService } from '@/services/paymentsService'
import { toast } from 'react-toastify'
import { FileUpload } from '@/components/ui/FileUpload'

interface PaymentProofUploadCardProps {
  applicationId?: string | null
}

const formatPaymentAmount = (payment: ApplicationPaymentModel): string => {
  if (payment.amount === null) return '—'
  const currency = payment.currency ?? 'NGN'
  return `${currency} ${payment.amount.toLocaleString()}`
}

const formatCurrencyValue = (amount?: number | null, currency?: string | null): string => {
  if (amount === null || amount === undefined) return '—'
  return `${currency ?? 'NGN'} ${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

const getBadgeClass = (status?: string | null): string => {
  const normalized = status?.toLowerCase()
  if (normalized === 'confirmed') {
    return 'bg-green-100 text-green-700'
  }
  if (normalized === 'rejected') {
    return 'bg-red-100 text-red-700'
  }
  return 'bg-yellow-100 text-yellow-700'
}

export const PaymentProofUploadCard: React.FC<PaymentProofUploadCardProps> = ({
  applicationId,
}) => {
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [errors, setErrors] = useState<{ amount?: string; reference?: string; file?: string }>({})

  const { data, isLoading, error } = useQuery<ApplicationModel>({
    queryKey: ['application', applicationId],
    queryFn: () => admissionsService.getApplicationById(applicationId!),
    enabled: Boolean(applicationId),
    staleTime: 1000 * 60 * 5,
  })

  const sortedPayments = useMemo(
    () =>
      (data?.payments ?? [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at ?? '').getTime() -
            new Date(a.created_at ?? '').getTime(),
        ),
    [data?.payments],
  )

  const paymentProgress = useMemo(() => {
    if (!data) return null
    const percent = data.paymentPercentComplete
    return {
      percent,
      paidAmount: data.totalPaidAmount,
      totalAmount: data.totalDueAmount,
      paidInstallments: data.paidInstallments,
      totalInstallments: data.totalInstallments,
      nextDueDate: data.nextInstallmentDueDate,
      installments: data.installments ?? [],
      currency: data.amount_currency ?? data.payments?.[0]?.currency ?? 'NGN',
    }
  }, [data])

  const mutation = useMutation({
    mutationFn: (payload: { amount: number; reference: string; file: File }) =>
      paymentsService.uploadPaymentProof(applicationId!, payload),
    onSuccess: () => {
      toast.success('Payment proof uploaded successfully')
      setAmount('')
      setReference('')
      setFile(null)
      setErrors({})
      queryClient.invalidateQueries({ queryKey: ['application', applicationId] })
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Unable to upload payment proof')
    },
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!applicationId) {
      return
    }
    const validationErrors: typeof errors = {}
    const parsedAmount = Number.parseFloat(amount)

    if (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      validationErrors.amount = 'Enter a valid amount'
    }
    if (!reference.trim()) {
      validationErrors.reference = 'Reference is required'
    }
    if (!file) {
      validationErrors.file = 'Please attach your receipt'
    }

    setErrors(validationErrors)

    if (Object.keys(validationErrors).length > 0) {
      return
    }

    if (!file) {
      return
    }

    mutation.mutate({
      amount: parsedAmount,
      reference: reference.trim(),
      file,
    })
  }

  return (
    <Card className="bg-white border border-gray-200">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-gray-900">
          Application Payments
        </CardTitle>
        <CardDescription>
          Upload proof of payment and monitor its verification status.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!applicationId ? (
          <p className="text-sm text-gray-600">
            Your profile is not linked to an application yet. Contact support if you
            believe this is a mistake.
          </p>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-600">
                Current status:{' '}
                <span className="font-medium text-gray-900">
                  {`${data?.payment_status||''}`.toUpperCase() || 'not provided'}
                </span>
              </p>
            </div>

            {paymentProgress && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Payment progress</p>
                    <p className="text-xs text-gray-500">
                      {paymentProgress.paidInstallments}/{paymentProgress.totalInstallments} installments •{' '}
                      {paymentProgress.nextDueDate
                        ? `Next due ${formatDateTime(paymentProgress.nextDueDate)}`
                        : 'No upcoming due date'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-[#7b2a40]">{paymentProgress.percent}%</p>
                    <p className="text-xs text-gray-500">
                      Paid {formatCurrencyValue(paymentProgress.paidAmount, paymentProgress.currency)} of{' '}
                      {formatCurrencyValue(paymentProgress.totalAmount, paymentProgress.currency)}
                    </p>
                  </div>
                </div>
                <div className="mt-3 h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-[#7b2a40] transition-all"
                    style={{ width: `${paymentProgress.percent}%` }}
                  />
                </div>
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount paid (NGN)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-[#7b2a40] ${
                      errors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                  {errors.amount && (
                    <p className="mt-1 text-xs text-red-600">{errors.amount}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment reference
                  </label>
                  <input
                    type="text"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-[#7b2a40] ${
                      errors.reference ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    placeholder="e.g. KBS-2025-000001"
                  />
                  {errors.reference && (
                    <p className="mt-1 text-xs text-red-600">{errors.reference}</p>
                  )}
                </div>
              </div>

              <FileUpload
                label="Upload Receipt"
                className="bg-[#7b2a40]/5 text-[#7b2a40] hover:bg-[#7b2a40]/10"
                accept=".pdf,.jpeg,.jpg,.png"
                onFileSelect={(files) => setFile(files?.[0] ?? null)}
                helperText={
                  <div className="space-y-1">
                    <p>Upload in .pdf, .jpeg or .png format.</p>
                    {file && (
                      <p className="text-xs text-slate-500">Selected: {file.name}</p>
                    )}
                    {errors.file && (
                      <p className="text-xs text-red-600">{errors.file}</p>
                    )}
                  </div>
                }
              />

              <button
                type="submit"
                disabled={mutation.isPending}
                className="w-full bg-[#7b2a40] text-white py-2 rounded-md text-sm font-medium hover:bg-[#652033] transition disabled:opacity-70"
              >
                {mutation.isPending ? 'Uploading…' : 'Upload payment proof'}
              </button>
            </form>

            <div>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">Payment history</p>
                {isLoading && (
                  <span className="text-xs text-gray-500">Refreshing…</span>
                )}
              </div>
              {error ? (
                <p className="text-sm text-red-600">
                  Unable to load payments: {error.message}
                </p>
              ) : sortedPayments.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No payment proof uploaded yet.
                </p>
              ) : (
                <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {sortedPayments.map((payment) => (
                    <div key={payment.id ?? payment.reference} className="p-3 text-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-900">{payment.reference}</p>
                          <p className="text-gray-600">{formatPaymentAmount(payment)}</p>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getBadgeClass(payment.status)}`}
                        >
                          {payment.status ?? 'pending'}
                        </span>
                      </div>
                      <div className="mt-2 grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
                        <p>Uploaded: {formatDateTime(payment.created_at)}</p>
                        <p>Verified: {formatDateTime(payment.verified_at)}</p>
                        {payment.verification_note && (
                          <p className="sm:col-span-2">
                            Note: {payment.verification_note}
                          </p>
                        )}
                        {payment.receipt_url && (
                          <p className="sm:col-span-2">
                            <a
                              href={payment.receipt_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline"
                            >
                              View receipt
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {paymentProgress?.installments?.length ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">Installments</p>
                  <p className="text-xs text-gray-500">
                    {paymentProgress.paidInstallments}/{paymentProgress.totalInstallments} completed
                  </p>
                </div>
                <div className="space-y-2">
                  {paymentProgress.installments.map((installment) => (
                    <div
                      key={installment.id ?? installment.installment_number}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">
                          Installment {installment.installment_number ?? '—'} ({installment.percentage ?? 0}%)
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                            installment.is_paid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {installment.is_paid ? 'Paid' : 'Pending'}
                        </span>
                      </div>
                      <div className="mt-1 grid gap-2 sm:grid-cols-3">
                        <span>Amount: {formatCurrencyValue(installment.amount, paymentProgress.currency)}</span>
                        <span>Paid: {formatCurrencyValue(installment.amount_paid, paymentProgress.currency)}</span>
                        <span>Due: {formatDateTime(installment.due_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
